import {DATE_FORMAT} from 'consts'
import {noop, omit} from 'lodash'
import moment from 'moment'
import React, {useCallback, useContext, useMemo, useReducer} from 'react'
import {Basic} from 'types'
import {GetterMode} from 'types/basic'
import {getter} from 'utils/getter'
import {hasGuests} from 'utils/guestCounterHelpers'
import {AppearanceProvider} from '../AppearanceProvider'
import {AutoCompleteHelper} from '../AutoCompleteHelper'
import {DEFAULT_CART_ITEM_INPUT} from './consts'
import {reducer} from './reducer'
import {InputContextValue} from './types'
import {getMonth} from './utils'

type Props = {
  children: React.ReactNode
  initialState: Basic.CartItem
}

const InputContext = React.createContext<InputContextValue>({
  input: DEFAULT_CART_ITEM_INPUT,
  mutators: {
    setOptionId: noop,
    setDate: noop,
    setAvailabilityId: noop,
    setUnitCounters: noop,
    setMonth: noop,
  },
})

export const InputProvider = ({children, initialState}: Props) => {
  const stepOrder = AppearanceProvider.useManager().stepOrder
  const changeLastOperation = AutoCompleteHelper.useChangeLastOperation()

  const initialDate = initialState.availabilityId ? moment(initialState.availabilityId).format(DATE_FORMAT) : ''
  const initialMonth = getMonth(initialState.availabilityId)

  const [input, dispatch] = useReducer(reducer, {
    ...initialState,
    date: initialDate,
    month: initialMonth,
  })

  const setOptionId = useCallback(
    (optionId: string) => {
      changeLastOperation('optionIdSelection')

      dispatch({type: 'setOptionId', optionId, stepOrder})
    },
    [changeLastOperation, stepOrder]
  )

  const setDate = useCallback(
    (date: string) => {
      changeLastOperation('dateSelection')

      dispatch({type: 'setDate', date, stepOrder})
    },
    [changeLastOperation, stepOrder]
  )

  const setAvailabilityId = useCallback(
    (availabilityId: string) => {
      changeLastOperation('availabilityIdSelection')

      dispatch({type: 'setAvailabilityId', availabilityId, stepOrder})
    },
    [changeLastOperation, stepOrder]
  )

  const setUnitCounters = useCallback(
    (unitCounters: Basic.UnitCounterMap) => {
      changeLastOperation('guestsSelection')

      dispatch({type: 'setUnitCounters', unitCounters, stepOrder})
    },
    [changeLastOperation, stepOrder]
  )

  const setMonth = useCallback((month: string) => dispatch({type: 'setMonth', month, stepOrder}), [stepOrder])

  const contextValue = useMemo(() => {
    return {
      input,
      mutators: {
        setOptionId,
        setDate,
        setAvailabilityId,
        setUnitCounters,
        setMonth,
      },
    }
  }, [input, setOptionId, setDate, setAvailabilityId, setUnitCounters, setMonth])

  return <InputContext.Provider value={contextValue}>{children}</InputContext.Provider>
}

InputProvider.useContext = () => {
  return useContext(InputContext)
}

InputProvider.useInput = () => {
  const context = InputProvider.useContext()
  return context.input
}

InputProvider.useCartItem = (): Basic.CartItem => {
  const context = InputProvider.useContext()
  return omit(context.input, ['date', 'month'])
}

InputProvider.useId = () => {
  const {id} = InputProvider.useInput()
  return id
}

InputProvider.useProductId = () => {
  const {productId} = InputProvider.useInput()
  return productId
}

InputProvider.useOptionId = (mode?: GetterMode) => {
  const {optionId} = InputProvider.useInput()
  return getter(optionId, mode, !!optionId, 'Option id is not set')
}

InputProvider.useDate = (mode?: GetterMode) => {
  const {date} = InputProvider.useInput()
  return getter(date, mode, !date, 'Date is not set')
}

InputProvider.useMonth = () => {
  const {month} = InputProvider.useInput()
  return month
}

InputProvider.useAvailabilityId = (mode?: GetterMode) => {
  const {availabilityId} = InputProvider.useInput()
  return getter(availabilityId, mode, !!availabilityId, 'Availability id is not set')
}

InputProvider.useUnitCounters = (mode?: GetterMode) => {
  const {unitCounters} = InputProvider.useInput()
  return getter(unitCounters, mode, hasGuests(unitCounters), 'Unit counters is not set')
}

InputProvider.useMutators = () => {
  const {mutators} = InputProvider.useContext()
  return mutators
}
